import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'

/**
 * Custom styles for NodeContentRenderer - Cohesive Paragraph Wrapper CSS
 * Forces inline flow for all content to maintain cohesive paragraph layout
 */
export const useCohesiveContentWrapperStyles = makeStyles(() => ({
    cohesiveContentWrapper: {
        // Main wrapper container - using && for higher specificity
        '&&': {
            display: 'block !important',
            width: '100%',
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
            wordBreak: 'normal !important',
            overflowWrap: 'break-word !important',
            lineHeight: 'inherit',
            textAlign: 'left',
            direction: 'ltr',
        },

        // Force pill-widgets and option elements to be inline - using higher specificity
        '&& .pill-widget, && [class*="option"], && [class*="Option"]': {
            display: 'inline-flex !important',
            verticalAlign: 'baseline !important',
        },

        // Material-UI Typography overrides - using higher specificity
        '&& .MuiTypography-root, && [class*="MuiTypography"]': {
            display: 'inline !important',
            verticalAlign: 'baseline !important',
        },

        // CKEditor inline forcing - using higher specificity
        '&& .ck-editor': {
            display: 'inline !important',
            verticalAlign: 'baseline !important',
            maxWidth: 'none !important',
        },

        '&& .ck-editor__main': {
            display: 'inline !important',
        },

        '&& .ck-content, && .ck-editor__editable': {
            display: 'inline !important',
            minHeight: 'auto !important',
            border: 'none !important',
            padding: '0 !important',
            margin: '0 !important',
            background: 'transparent !important',
            boxShadow: 'none !important',
            verticalAlign: 'baseline !important',
            whiteSpace: 'normal !important',
        },

        // Pills and selection options inline - using higher specificity
        '&& .pill, && [class*="pill"], && [class*="selection-option"], && [class*="replacement-option"]': {
            display: 'inline-flex !important',
            verticalAlign: 'baseline !important',
            whiteSpace: 'nowrap',
            margin: '0 2px',
            padding: '2px 8px',
            borderRadius: '12px',
            lineHeight: 'inherit !important',
        },

        // Option groups inline - using higher specificity
        '&& [class*="option-group"], && [class*="OptionGroup"]': {
            display: 'inline !important',
            verticalAlign: 'baseline !important',
        },

        // Track changes preservation - keep inline - using higher specificity
        '&& [class*="addedText"], && .addedText': {
            display: 'inline !important',
            background: '#C3FFCC',
            color: '#0D7B55',
            verticalAlign: 'baseline !important',
        },

        '&& [class*="removedText"], && .removedText': {
            display: 'inline !important',
            color: '#D43636',
            background: '#F6CAD5',
            textDecoration: 'line-through',
            textDecorationColor: '#D43636',
            verticalAlign: 'baseline !important',
        },

        // Assembly Text Input overrides - using higher specificity
        '&& [class*="assembly-text"], && .assembly-text': {
            display: 'inline-block !important',
            width: '514px',
            minHeight: '36px',
            margin: '10px 20px',
            fontSize: '14px',
            '& .MuiOutlinedInput-root': {
                display: 'inline-block !important',
                verticalAlign: 'baseline !important',
                width: '100%',
                padding: '10px 11px',
                fontSize: '14px',
            },
        },

        // Units of measure inline-flex - using higher specificity to prevent text cutoff
        '&& [class*="units-of-measure"], && [class*="UnitsOfMeasure"], && [data-testid*="units-of-measure"]': {
            display: 'inline-flex !important',
            verticalAlign: 'baseline !important',
            flexDirection: 'column !important',
        },

        // Prevent any flex or grid layouts within - using higher specificity
        '&& *': {
            flex: 'none !important',
            grid: 'none !important',
        },

        // Ensure no block-level elements break the flow - using higher specificity
        '&& div, && span, && p': {
            display: 'inline',
            verticalAlign: 'baseline !important',
        },

        // Special handling for contentEditable elements - using higher specificity
        '&& [contenteditable="true"]': {
            display: 'inline !important',
            verticalAlign: 'baseline !important',
            minHeight: 'auto !important',
            whiteSpace: 'normal !important',
        },

        // Prevent line breaks from components
        '& br': {
            display: 'none',
        },

        // Handle focus states without breaking layout - using higher specificity
        // data-uom-pill-input is used instead of class*= because MUI v4 generates short class names
        // in production (e.g. jss-123) that never contain the key string, so class*= exclusions
        // only work in development where names are descriptive.
        '&& *:focus:not(.optionItem):not([class*="optionItem"]):not([class*="editableTextBoxed"]):not(.editableTextBoxed):not([data-uom-pill-input])': {
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
        },

        // Specific overrides for common breaking patterns - using higher specificity
        '&& [class*="displayWrap"], && [class*="display-wrap"]': {
            display: 'inline !important',
            flexFlow: 'none !important',
            gap: '0 !important',
        },

        // Ensure inline-flex elements become inline - using higher specificity
        '&& [style*="display: inline-flex"], && [style*="display:inline-flex"]': {
            display: 'inline !important',
        },

        // Special handling for non-keyed hyperlinks - allow them to have margin
        '&& .non-keyed-hyperlink-wrapper': {
            display: 'inline-flex !important',
            marginTop: '2px !important',
            marginBottom: '2px !important',
            marginLeft: pxToRem(40),
        },

        '&& [data-testid*="replacement-option"]': {
            display: 'inline-flex !important',
            flexDirection: 'column !important',
        },
    },
}))
